import { SitesService } from './../../../shared/services/sites.service';
import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { Site } from '@classes/site';
import { AddressesService } from '@services/addresses.service';
import { Address } from '@classes/address';
import { AddressGroup } from '@classes/address-group';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

interface CivilityItem { id: number; name: string; }

@Component({
  selector: 'app-address-edit',
  templateUrl: './address-edit.component.html',
  styleUrls: ['./address-edit.component.scss']
})
export class AddressEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() address: Address;
  sites: Site[] = [];
  addressGroups: AddressGroup[] = [];

  isSender = false;
  isReceiver = false;
  stringMaxLength = 50;
  codeMaxLength = 10;

  tagPattern = "^[a-z0-9]{0,50}$";
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private addressSrv: AddressesService,
    private sitesSrv: SitesService,
    private route: ActivatedRoute,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  initSub(): void {
    this.isLoading = true;
    this.addressGroups = this.route.snapshot.data.addressGroups || [];
    this.sitesSrv.getList().subscribe(sitesFromSrv => {
      this.sites = sitesFromSrv;
      this.isLoading = false;

      this.initForm();
    });
  }

  get tags() {
    return this.editForm.get('tags');
  }

  get services() {
    return this.editForm.get('services');
  }

  onSubmit(): void {
    const sites = this.editForm.value.sites || [];
    const addressGroups = this.editForm.value.addressGroups || [];
    if (!sites.length && !addressGroups.length) {
      this.uiSrv.showSnackbar('repository.addresses.edit.associationRequired', true);
      return;
    }

    this.isLoading = true;
    if (this.address) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const addressCopy: Address = JSON.parse(JSON.stringify(this.address));
    addressCopy.name = this.editForm.value.name;
    addressCopy.code = this.editForm.value.code;
    addressCopy.street = this.editForm.value.street;
    addressCopy.streetNumber = this.editForm.value.streetNumber;
    addressCopy.locality = this.editForm.value.locality;
    addressCopy.postalCode = this.editForm.value.postalCode;
    addressCopy.isSender = this.editForm.value.isSender;
    addressCopy.isReceiver = this.editForm.value.isReceiver;
    addressCopy.sites = this.editForm.value.sites;
    addressCopy.phone = this.editForm.value.phone;
    addressCopy.tags = this.editForm.value.tags;
    addressCopy.services = this.editForm.value.services;
    addressCopy.addressGroupIds = this.getSelectedAddressGroupIds();
    addressCopy.addressGroups = [];

    this.addressSrv.put(addressCopy).subscribe(() => this.isLoading = false);
  }

  private create(): void {
    this.address = new Address(
      this.editForm.value.name,
      this.editForm.value.street,
      this.editForm.value.streetNumber,
      this.editForm.value.locality,
      this.editForm.value.postalCode,
      this.editForm.value.isSender,
      this.editForm.value.isReceiver,
      this.editForm.value.sites,
      this.editForm.value.phone,
      this.editForm.value.tags,
      this.editForm.value.services,
    );
    this.address.code = this.editForm.value.code;
    this.address.addressGroupIds = this.getSelectedAddressGroupIds();

    this.addressSrv.post(this.address)
      .subscribe(
        () => this.isLoading = false,
        () => this.address = null
      );
  }

  private initForm(): void {
    let name = "";
    let code = "";
    let street = "";
    let streetNumber = "";
    let locality = "";
    let postalCode = "";
    let isSender = false;
    let isReceiver = false;
    let sites: Site[] = [];
    let phone = '';
    let tags = [];
    let services = [];
    let addressGroups: AddressGroup[] = [];

    if (this.address) {
      name = this.address.name;
      code = this.address.code;
      street = this.address.street;
      streetNumber = this.address.streetNumber;
      locality = this.address.locality;
      postalCode = this.address.postalCode;
      isSender = this.address.isSender;
      isReceiver = this.address.isReceiver;
      sites = this.address.sites;
      phone = this.address.phone;
      tags = this.address.tags;
      services = this.address.services;
      addressGroups = this.address.addressGroups || [];
    }

    this.editForm = this.fb.group({
      name: [name, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      code: [code, [Validators.maxLength(this.codeMaxLength)]],
      street: [street, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      streetNumber: [streetNumber, [Validators.maxLength(this.stringMaxLength)]],
      locality: [locality, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      postalCode: [postalCode, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      isSender: [isSender],
      isReceiver: [isReceiver],
      sites: [sites],
      phone: [phone, [Validators.maxLength(this.stringMaxLength)]],
      tags: new UntypedFormControl(tags),
      services: new UntypedFormControl(services),
      addressGroups: [addressGroups]
    });

  }

  addTagOrService(field: string, event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    const entity = field === 'tags' ? this.tags : this.services;

    // Add our tag or service
    if ((value || '').trim()) {
      if (value.length <= 50) {
        entity.setValue([...entity.value, value.trim()]);
        entity.updateValueAndValidity();
      }

    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  removeTagOrService(field: string, object: string): void {
    const index = this.editForm.value[field].indexOf(object);

    const entity = field === 'tags' ? this.tags : this.services;

    if (index >= 0) {
      entity.value.splice(index, 1);
      entity.updateValueAndValidity();
    }
  }

  private getSelectedAddressGroupIds(): string[] {
    return this.editForm.value.addressGroups.map((group: AddressGroup) => group.id);
  }
}
